import { i18n } from "@lingui/core";
import { msg, plural } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import { observer } from "mobx-react-lite";
import { useState } from "react";

import { AuditTables } from "../../../api";
import { AppModel } from "../../../app/app-model";
import { Button } from "../../../components/ui-kit/button";
import { Combobox } from "../../../components/ui-kit/fields/combobox";
import { CellJSON } from "../../../components/ui-kit/tables/cells/cell-json";
import { DataTable } from "../../../components/ui-kit/tables/data-table";
import {
  uniqueArrayValueOptions,
  uniqueValueOptions,
} from "../../../utils/filter-presets";
import { assert } from "../../../utils/utils";
import { PatientSubpage } from "../common/patient-subpage";
import { Logs } from "./logs";

export const LogsTable = observer(function LogsTable(props: { app: AppModel }) {
  const { t } = useLingui();
  const patient = props.app.requirePatient();
  const [dataType, setDataType] = useState<AuditTables | null>(null);
  const [dataModel, setDataModel] = useState<Logs | undefined>();

  return (
    <PatientSubpage title={t`Logs`}>
      <div>
        <label>
          <Trans>Data type:</Trans>{" "}
          <Combobox
            value={dataType as string}
            onChange={(newValue) => {
              setDataType(newValue as AuditTables);
            }}
            options={auditTableOptions.map((o) => ({
              value: o.value,
              label: i18n._(o.label),
            }))}
            placeholder={t`Select data type`}
          />
        </label>{" "}
        <Button
          disabled={!dataType}
          onClick={() => {
            assert(dataType, "Data type must be selected");
            const model = patient.loadLogs(dataType);
            setDataModel(model);
          }}
        >
          <Trans>Load</Trans>
        </Button>
      </div>

      {dataModel != null && (
        <>
          <br className="h-10" />

          <DataTable
            persistState
            model={dataModel}
            itemKeyField="uuid"
            filterNoun={(count) =>
              t`${plural(count, { one: "log", other: "logs" })}`
            }
            columns={[
              {
                header: <Trans>Table</Trans>,
                attr: "audit_table",
                type: "string",
              },
              {
                header: <Trans>Timestamp</Trans>,
                attr: "audit_item_timestamp",
                type: "datetime",
                sort: true,
                initSort: "desc",
              },
              {
                header: <Trans>Type</Trans>,
                attr: "audit_item_type",
                type: "string",
                filter: {
                  kind: "multi",
                  group: t`Log`,
                  options: uniqueValueOptions("audit_item_type"),
                },
              },
              {
                header: <Trans>Action</Trans>,
                cell: ({ item }) => <CellJSON value={item.audit_item_action} />,
              },
              {
                header: <Trans>Tags</Trans>,
                attr: "tags",
                type: "array",
                filter: {
                  kind: "multi",
                  group: t`Log`,
                  options: uniqueArrayValueOptions("tags"),
                },
              },
            ]}
            verticalAlign="top"
          />
        </>
      )}
    </PatientSubpage>
  );
});

const auditTableOptions = [
  { value: AuditTables.users, label: msg`Users` },
  { value: AuditTables.patients, label: msg`Patients` },
  { value: AuditTables.diagnoses, label: msg`Diagnoses` },
  { value: AuditTables.medicaldata, label: msg`Medical data` },
  { value: AuditTables.medications, label: msg`Medications` },
  { value: AuditTables.symptoms, label: msg`Symptoms` },
  { value: AuditTables.ppg, label: msg`PPG` },
  { value: AuditTables.ppg_derived, label: msg`PPG derived` },
  { value: AuditTables.sq_computed, label: msg`SQ computed` },
  { value: AuditTables.patient_schedule, label: msg`Patient schedule` },
  { value: AuditTables.simple_messaging, label: msg`Simple messaging` },
  { value: AuditTables.patient_alerts, label: msg`Patient alerts` },
  { value: AuditTables.patient_thresholds, label: msg`Patient thresholds` },
  { value: AuditTables.patient_events, label: msg`Patient events` },
  { value: AuditTables.medical_reports, label: msg`Medical reports` },
];
